import { motion } from "framer-motion";
import { MessageCircle, CreditCard, Trophy, CheckCircle2 } from "lucide-react";
import { RAFFLE } from "@/lib/raffleData";

const STEPS = [
  {
    icon: MessageCircle,
    step: "01",
    title: "Escríbenos por WhatsApp",
    text: `Contáctanos al ${RAFFLE.whatsappDisplay} y solicita el número de ficha que quieras.`,
    color: "#25D366",
  },
  {
    icon: CreditCard,
    step: "02",
    title: "Realiza tu pago",
    text: "Transferencia, Nequi, Daviplata o efectivo. Confirmamos al instante.",
    color: "#F0B91B",
  },
  {
    icon: CheckCircle2,
    step: "03",
    title: "Recibe tu ficha digital",
    text: "Te enviamos tu ficha con número único registrado a tu nombre.",
    color: "#E63946",
  },
  {
    icon: Trophy,
    step: "04",
    title: "Espera el sorteo",
    text: `El ${RAFFLE.drawDate} transmitimos en vivo. ¡El ganador se lleva todo!`,
    color: "#7FD1AE",
  },
];

export default function HowToParticipate() {
  return (
    <section
      id="como"
      className="relative py-24 md:py-32 overflow-hidden"
      data-testid="how-section"
    >
      <div className="absolute inset-0 bg-gradient-to-b from-[#0A2818] to-[#051A0F]" />
      <div
        className="absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage:
            "linear-gradient(#F0B91B 1px, transparent 1px), linear-gradient(90deg, #F0B91B 1px, transparent 1px)",
          backgroundSize: "60px 60px",
        }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-6 md:px-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16 md:mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#F0B91B]/30 bg-[#F0B91B]/5 mb-6">
            <span className="text-xs uppercase tracking-[0.3em] text-[#F0B91B] font-semibold font-outfit">
              Cómo participar
            </span>
          </div>
          <h2 className="font-bebas text-5xl md:text-7xl text-[#FDFBF7] tracking-wider leading-[0.9]">
            Cuatro capítulos <br className="hidden md:block" />y estás{" "}
            <span className="gold-shimmer">dentro</span>
          </h2>
          <p className="mt-5 text-lg text-[#FDFBF7]/70 max-w-2xl font-outfit">
            Adquirir tu ficha es tan fácil como enviar un mensaje. Sin registros complicados,
            sin trámites.
          </p>
        </motion.div>

        {/* Numbered manifesto chapters */}
        <div className="divide-y divide-[#F0B91B]/12 border-t border-[#F0B91B]/12">
          {STEPS.map((s, i) => (
            <motion.div
              key={s.step}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
              className="group grid grid-cols-[auto_1fr] md:grid-cols-[10rem_auto_1fr] gap-5 md:gap-8 items-start md:items-center py-8 md:py-10"
              data-testid={`step-${s.step}`}
            >
              <div className="chapter-index text-6xl md:text-8xl leading-none select-none transition-all duration-500 group-hover:[-webkit-text-stroke-color:rgba(240,185,27,0.9)]">
                {s.step}
              </div>
              <div
                className="hidden md:flex w-14 h-14 rounded-2xl items-center justify-center border transition-transform duration-500 group-hover:scale-110"
                style={{ backgroundColor: `${s.color}15`, borderColor: `${s.color}40` }}
              >
                <s.icon className="w-7 h-7" style={{ color: s.color }} />
              </div>
              <div className="col-span-1">
                <div className="flex items-center gap-3">
                  <span
                    className="md:hidden w-9 h-9 rounded-xl flex items-center justify-center border flex-shrink-0"
                    style={{ backgroundColor: `${s.color}15`, borderColor: `${s.color}40` }}
                  >
                    <s.icon className="w-5 h-5" style={{ color: s.color }} />
                  </span>
                  <h3 className="font-bebas text-3xl md:text-4xl text-[#FDFBF7] tracking-wide">
                    {s.title}
                  </h3>
                </div>
                <p className="mt-2 text-base text-[#FDFBF7]/70 font-outfit leading-relaxed max-w-2xl">
                  {s.text}
                </p>
              </div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="mt-14 text-center"
        >
          <a
            href="#elegir-ficha"
            className="inline-flex items-center gap-3 bg-[#F0B91B] hover:bg-[#FFD700] text-[#051A0F] px-8 py-4 rounded-full font-bebas text-2xl tracking-wider shadow-[0_15px_40px_-10px_rgba(240,185,27,0.6)] hover:-translate-y-1 transition-all"
            data-testid="how-cta"
          >
            <MessageCircle className="w-6 h-6" />
            Elegir mi ficha y participar
          </a>
        </motion.div>
      </div>
    </section>
  );
}
